/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OperationLaborTimeItem } from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { SFlex } from 'components/ReferencePriceSection/ReferencePriceSection.styled';
import LaborTimeCard from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/LaborTimesSelection/LaborTimeCard';
import { InfoAlertWrapper } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PartsSelection.styled';
import {
  getPartPosition,
  parseLaborTimeDesignation,
  useSortLaborTimes,
  getHasMultipleIdenticalLaborTimes,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/utils';
import { theme } from 'styles';
import { InfoAlert, MarginBox, Text } from 'UI';

const MULTIPLE_SO = 'multiple_so';
const IDENTICAL_LT = 'identical_lt';

const LaborTimesSelectionGroup = ({
  laborTimeGroup,
  mainGOId,
}: {
  laborTimeGroup: OperationLaborTimeItem;
  mainGOId: string | undefined;
}) => {
  const { t } = useTranslation();
  const sortedLaborTimes = useSortLaborTimes(laborTimeGroup.laborTimes);
  const hasMultipleLaborTimes = sortedLaborTimes.length > 1;
  const hasMainGo = sortedLaborTimes.some((lt) => lt.id === mainGOId);
  const showIdenticalLaborTimesAlert = !hasMainGo && getHasMultipleIdenticalLaborTimes(sortedLaborTimes);
  const soCount = sortedLaborTimes.filter((lt) => lt.operationType === '2').length;
  const showMultipleSOAlert = !hasMainGo && soCount > (showIdenticalLaborTimesAlert ? 2 : 1);

  const getDesignation = (designation: string, index: number) => {
    const firstLaborTime = laborTimeGroup.laborTimes.at(0);
    const hasAllSameOperations = laborTimeGroup.laborTimes.every(
      (lt) => lt.operation === firstLaborTime?.operation && lt.element === firstLaborTime?.element,
    );

    if (hasAllSameOperations && index > 0) {
      return null;
    }

    return (
      <MarginBox ml={40}>
        <Text type="lg" bold disableGutter transform={'sentence-case'}>
          {parseLaborTimeDesignation(designation)}
        </Text>
      </MarginBox>
    );
  };

  const isOnlySOLaborTime = () => {
    if (hasMainGo) {
      return false;
    }
    const soLaborTimes = laborTimeGroup.laborTimes.filter((lt) => lt.operationType === '2');
    return soLaborTimes.length === 1;
  };

  const renderAlert = (type: typeof MULTIPLE_SO | typeof IDENTICAL_LT) => {
    return (
      <InfoAlertWrapper>
        <InfoAlert
          size={'lg'}
          backgroundColor={theme.color.ice_blue}
          message={
            <SFlex direction={'column'}>
              <Text type="base" bold>
                {t(
                  'catalog.easy_basket.labor_time_part.multipart.alert.title',
                  'The following elements need your attention',
                )}
              </Text>
              {type === MULTIPLE_SO && (
                <Text type={'search_result'}>
                  {t(
                    'catalog.easy_basket.labor_time_part.multiple_so.alert.description',
                    'Several labor times are available, Please make your selection.',
                  )}
                </Text>
              )}
              {type === IDENTICAL_LT && (
                <Text type={'search_result'}>
                  {t(
                    'catalog.easy_basket.labor_time_part.multipart.alert.description',
                    'Several identical Labor times, Please make your selection.',
                  )}
                </Text>
              )}
            </SFlex>
          }
        />
      </InfoAlertWrapper>
    );
  };

  return (
    <>
      {showMultipleSOAlert && !showIdenticalLaborTimesAlert && renderAlert(MULTIPLE_SO)}
      {showIdenticalLaborTimesAlert && renderAlert(IDENTICAL_LT)}
      {sortedLaborTimes.map((laborTime, index) => (
        <React.Fragment key={`${laborTime.id}_${index}`}>
          {getDesignation(laborTime.designation ?? '', index)}
          <LaborTimeCard
            laborTime={laborTime}
            itemGroup={laborTimeGroup.itemGroup}
            isMultipleLaborTimesGroup={hasMultipleLaborTimes}
            mainLaborTimePosition={getPartPosition(index, sortedLaborTimes.length)}
            isMainGOLaborTime={laborTime.id === mainGOId}
            isOnlySOLaborTime={isOnlySOLaborTime()}
          />
        </React.Fragment>
      ))}
    </>
  );
};

export default LaborTimesSelectionGroup;
